import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Buildings, House, Users, CalendarBlank, ClipboardText, UserCircle,
  SignOut, List, X, Moon, Sun, FolderLock, Key, Money, ChatCircleDots, Megaphone, Wallet,
  Timer, CalendarCheck,
} from "@phosphor-icons/react";
import NotificationBell from "@/components/NotificationBell";

const ROLE_LABEL = { admin: "Administrator", manager: "Manager", supervisor: "Supervisor", employee: "Employee" };

function useDarkMode() {
  const [dark, setDark] = useState(document.documentElement.classList.contains("dark"));
  const toggle = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
  };
  return [dark, toggle];
}

export default function Layout({ children }) {
  const { user, logout, canApprove } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [dark, toggleDark] = useDarkMode();
  const isAdmin = user?.role === "admin";
  const canSchedules = isAdmin || user?.role === "manager" || user?.role === "supervisor";

  const nav = [
    { to: "/dashboard", label: "Dashboard", icon: House },
    { to: "/directory", label: "Directory", icon: Users },
    ...(canApprove ? [{ to: "/file201", label: "201 Files", icon: FolderLock }] : []),
    ...(!isAdmin ? [{ to: "/leave", label: "My Leave", icon: CalendarBlank }] : []),
    { to: "/payroll", label: "Payroll", icon: Money },
    { to: "/loans", label: "Loans", icon: Wallet },
    { to: "/memos", label: "Notices", icon: Megaphone },
    ...(!isAdmin ? [{ to: "/overtime", label: "File Overtime", icon: Timer }] : []),
    ...(canSchedules ? [{ label: "Schedules", icon: CalendarCheck, children: [
      { to: "/schedules/overtime", label: "Overtime Record" },
      { to: "/schedules/activity", label: "Activity Schedule" },
    ] }] : []),
    ...(canApprove ? [{ to: "/approvals", label: "Approvals", icon: ClipboardText }] : []),
    ...(isAdmin ? [{ to: "/inquiries", label: "Inquiries", icon: ChatCircleDots }] : []),
    ...(isAdmin ? [{ to: "/users", label: "User Accounts", icon: Key }] : []),
    ...(!isAdmin ? [{ to: "/profile", label: "Profile", icon: UserCircle }] : []),
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = (user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  const SidebarContent = (
    <>
      <div className="flex items-center gap-2 px-6 h-16 border-b border-border">
        <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
          <Buildings weight="duotone" className="h-5 w-5 text-primary-foreground" />
        </div>
        <div className="flex flex-col leading-tight">
          <span className="font-heading font-extrabold text-base tracking-tight">Casedist Inc</span>
          <span className="text-xs text-muted-foreground font-medium">HR Management</span>
        </div>
      </div>
      <nav className="flex-1 px-3 py-6 space-y-1">
        {nav.map((item) => item.children ? (
          <div key={item.label} className="pt-2">
            <div className="flex items-center gap-3 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground/70">
              <item.icon weight="duotone" className="h-5 w-5" />
              {item.label}
            </div>
            <div className="ml-4 pl-3 border-l border-border space-y-1 mt-1">
              {item.children.map((c) => (
                <NavLink key={c.to} to={c.to} data-testid={`nav-${c.label.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `block px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150 ${
                      isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground"
                    }`
                  }>
                  {c.label}
                </NavLink>
              ))}
            </div>
          </div>
        ) : (
          <NavLink key={item.to} to={item.to} data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground"
              }`
            }>
            <item.icon weight="duotone" className="h-5 w-5" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 pb-4">
        <div className="flex items-center gap-3 px-3 py-3 rounded-lg bg-muted/60 border border-border">
          <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm shrink-0">
            {user?.picture ? <img src={user.picture.startsWith("http") ? user.picture : `${process.env.REACT_APP_BACKEND_URL}${user.picture}`} alt="" className="h-9 w-9 rounded-full object-cover" /> : initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground">{ROLE_LABEL[user?.role]}</p>
          </div>
          <Button variant="ghost" size="icon" data-testid="logout-button" onClick={handleLogout} className="h-8 w-8 shrink-0">
            <SignOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-col fixed inset-y-0 border-r border-border bg-card">
        {SidebarContent}
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 flex flex-col bg-card border-r border-border">
            {SidebarContent}
          </aside>
        </div>
      )}

      <div className="flex-1 lg:pl-64 flex flex-col min-w-0">
        <header className="h-16 sticky top-0 z-30 flex items-center justify-between px-4 sm:px-8 border-b border-border bg-card/70 backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="lg:hidden" data-testid="menu-button" onClick={() => setOpen(true)}>
              {open ? <X className="h-5 w-5" /> : <List className="h-5 w-5" />}
            </Button>
            <span className="font-heading font-bold text-base sm:text-lg tracking-tight">
              {ROLE_LABEL[user?.role]} Workspace
            </span>
          </div>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <Button variant="ghost" size="icon" data-testid="theme-toggle" onClick={toggleDark}>
              {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-8 max-w-7xl w-full mx-auto">{children}</main>
      </div>
    </div>
  );
}
